// Write-ahead marker for a NATIVE (mobile-app) recording. The native path has
// no segment streaming, no IndexedDB write-ahead, and no finalless recovery in
// the webview — a webview reset mid-recording silently orphans the recording
// (incident 2026-07-21, chat 16:15835149: ~17 min lost). localStorage survives
// webview resets, so a marker written at start and cleared only when the audio
// is handed back makes the orphan observable: marker present + not recording
// on mount = a recording was lost mid-flight.
//
// All storage access is guarded — on locked-down browsers the feature is
// silently off (matches recordingAutoRecoverFlag's fail-closed pattern).

import { rememberNativeRecordingId } from "./nativeRecordingIdentity";

export const NATIVE_RECORDING_MARKER_KEY = "nativeActiveRecording";

// Never surface a marker younger than this: it may belong to a recording whose
// start handshake is still in flight on this very page load.
export const MARKER_GRACE_MS = 15000;

// ...and never passively surface one older than this. The grace window is only a
// FLOOR; without a ceiling a marker whose recording was orphaned long ago kept
// surfacing the blocking lost-recording modal on every mount, forever, and grew
// *more* likely to fire the older it got — because the one path that clears it
// (native handing the audio back) is exactly what a crashed or abandoned
// recording never runs.
//
// A day-old orphan is still worth prompting about; a week-old one will not be
// resolved by a modal. Expiring only stops the prompt: the audio stays on the
// device and remains reachable from the recovery bar and Local Recordings.
// Explicit start guards still read expired markers: age cannot prove that the
// previous recording was recovered, so it cannot authorize replacing its marker.
export const MARKER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function writeNativeRecordingMarker(marker) {
  try {
    localStorage.setItem(NATIVE_RECORDING_MARKER_KEY, JSON.stringify(marker));
    return true;
  } catch (_) {
    return false;
  }
}

// How long a Local Recordings check keeps counting as evidence for the
// no-recording release. "The user looked days ago" cannot vouch for what is
// on the phone today; past this, the prompt asks for a fresh check.
export const LOCAL_RECORDINGS_CHECK_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Raw read: no user/grace filtering. Used by the rescue flow to check whether
// the audioRecorded handler cleared the marker (= rescue succeeded).
export function peekNativeRecordingMarker() {
  try {
    const raw = localStorage.getItem(NATIVE_RECORDING_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.recordingUuid) {
      clearNativeRecordingMarker();
      return null;
    }
    return parsed;
  } catch (_) {
    // Malformed JSON: self-heal so it can't wedge every future read.
    clearNativeRecordingMarker();
    return null;
  }
}

// Filtered read for the detection surfaces. Only the owning user's marker
// (shared devices: another user's marker is preserved, not shown, not cleared)
// and normally only past the grace window. Explicit start guards also need
// recent and expired markers: a new capture must not replace an unresolved
// recording's marker just because its handoff is very new or very old.
export function readNativeRecordingMarker(userId, { now = Date.now(), ignoreGrace = false, ignoreAge = false } = {}) {
  const marker = peekNativeRecordingMarker();
  if (!marker) return null;
  if (!userId || marker.userId !== userId) return null;
  // Grace from the most recent lifecycle event: a marker freshly started OR
  // freshly stopped (handoff in flight) is not an orphan yet.
  const lastEventAt = Math.max(marker.startedAt || 0, marker.stoppedAt || 0);
  if (!ignoreGrace && now - lastEventAt < MARKER_GRACE_MS) return null;
  // Aged out: stop prompting. Deliberately not cleared — peek() is the rescue
  // flow's raw read and other recovery surfaces still rely on the marker being
  // there. Filtering is this function's job; deleting is not.
  if (!ignoreAge && now - lastEventAt > MARKER_MAX_AGE_MS) return null;
  return marker;
}

// Stamp the moment a normal stop was requested. From here the native handoff
// is in flight — the grace window restarts from this stamp so a healthy
// stop→handoff (which can take several seconds for long recordings) is never
// misread as an orphan. If the handoff dies (crash mid-transfer), the stamp
// ages out and the marker correctly reads stale again.
export function markNativeRecordingMarkerStopped() {
  const marker = peekNativeRecordingMarker();
  if (!marker) return;
  writeNativeRecordingMarker({ ...marker, stoppedAt: Date.now() });
}

const hasRecordingId = (recordingId) =>
  (typeof recordingId === "string" && recordingId.trim().length > 0) ||
  (typeof recordingId === "number" && Number.isFinite(recordingId) && recordingId > 0);

// The installed shell's manual Retry omits the UUID. Remember its row id only
// after a UUID-bearing payload establishes that the two ids name this recording.
// Persist before conversion/upload: a failed transfer can be retried after reload.
export function bindNativeRecordingId(recordingUuid, recordingId, userId) {
  const marker = peekNativeRecordingMarker();
  if (
    marker?.userId === userId &&
    marker?.recordingUuid === recordingUuid &&
    hasRecordingId(marker.nativeRecordingId) &&
    String(marker.nativeRecordingId) !== String(recordingId)
  )
    return null;
  // Automatic sync also replays older recordings with no pending marker. Their
  // later backend completion still needs the exact native row for its ack.
  rememberNativeRecordingId(recordingUuid, recordingId, userId);
  if (!userId || marker?.userId !== userId || marker?.recordingUuid !== recordingUuid || !hasRecordingId(recordingId))
    return null;
  const updated = { ...marker, nativeRecordingId: recordingId };
  writeNativeRecordingMarker(updated);
  return updated;
}

export function resolveNativeRecordingUuid(data, userId) {
  if (data?.recordingUuid) return data.recordingUuid;
  const marker = peekNativeRecordingMarker();
  if (!userId || marker?.userId !== userId || marker?.chatId !== data?.chatId) return undefined;
  if (!hasRecordingId(marker.nativeRecordingId) || !hasRecordingId(data?.recordingId)) return undefined;
  return String(marker.nativeRecordingId) === String(data.recordingId) ? marker.recordingUuid : undefined;
}

// A legacy retry without a known row/UUID mapping cannot prove which of an
// encounter's recordings was saved. Retain the guard and offer explicit user
// verification only after a successful save. This is never an automatic unlock.
export function markNativeRecoveryCandidate(recordingUuid, recordingId, userId) {
  const marker = peekNativeRecordingMarker();
  if (!userId || marker?.userId !== userId || marker?.recordingUuid !== recordingUuid || !hasRecordingId(recordingId))
    return null;
  if (hasRecordingId(marker.nativeRecordingId) && String(marker.nativeRecordingId) !== String(recordingId)) return null;
  const updated = { ...marker, recoveryCandidate: { recordingId, savedAt: Date.now() } };
  writeNativeRecordingMarker(updated);
  return updated;
}

// Stamps are written only onto the marker they were earned for: a tap on an
// old prompt must never lend its evidence to a newer recording's marker.
const stampNativeRecordingMarker = (recordingUuid, field) => {
  const marker = peekNativeRecordingMarker();
  if (!recordingUuid || marker?.recordingUuid !== recordingUuid) return null;
  const updated = { ...marker, [field]: Date.now() };
  return writeNativeRecordingMarker(updated) ? updated : null;
};

// Evidence, not proof: the shell has no "sync complete" or recording-list
// message, so the user opening Local Recordings for this marker is the most
// the webview can know before offering the no-recording release.
export function markNativeLocalRecordingsChecked(recordingUuid) {
  return stampNativeRecordingMarker(recordingUuid, "localRecordingsCheckedAt");
}

// Dedupes the dead-end diagnostic across reloads — the prompt re-surfaces on
// every record press and mount while the marker stands.
export function markNativeMarkerDeadEndReported(recordingUuid) {
  return stampNativeRecordingMarker(recordingUuid, "deadEndReportedAt");
}

// With an id: clear only if the stored marker matches, so a stale flow (an old
// rescue timeout, a late error handler) can never erase a NEWER recording's
// marker. Without an id: unconditional (for invalid-marker cleanup).
export function clearNativeRecordingMarker(recordingUuid) {
  try {
    if (recordingUuid) {
      const raw = localStorage.getItem(NATIVE_RECORDING_MARKER_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.recordingUuid && parsed.recordingUuid !== recordingUuid) return;
        } catch (_) {}
      }
    }
    localStorage.removeItem(NATIVE_RECORDING_MARKER_KEY);
  } catch (_) {}
}
